import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useApp } from "@/components/AppShell";
import { useSettings } from "@/lib/settings";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { initials } from "@/lib/meta";
import { UserPlus, Plus, Trash2, GripVertical, Pencil, ChevronUp, ChevronDown, Users, Palette, Layers, ListChecks, Mail, Send, CheckCircle2, AlertCircle, RefreshCw, Clock } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const ROLES = ["admin", "developer", "pm", "designer", "qa", "viewer"];
const COLORS = ["red", "orange", "amber", "emerald", "sky", "purple", "slate", "blue", "pink", "indigo"];
const CATEGORIES = [{ id: "todo", label: "To Do" }, { id: "inprogress", label: "In Progress" }, { id: "done", label: "Done" }];

const TABS = [
  { id: "users", label: "Users", icon: Users },
  { id: "statuses", label: "Statuses", icon: ListChecks },
  { id: "priorities", label: "Priorities", icon: Palette },
  { id: "types", label: "Issue Types", icon: Layers },
  { id: "email", label: "Email & SMTP", icon: Mail },
];

export default function Admin() {
  const [tab, setTab] = useState("users");
  return (
    <div className="px-6 py-6 max-w-[1200px]">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-widest text-muted-foreground">Configuration</div>
        <h1 className="font-display text-3xl font-semibold tracking-tight">Administration</h1>
      </div>
      <div className="flex items-center gap-1 mb-4 border-b border-border">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`admin-tab-${t.id}`}
              className={cn("flex items-center gap-1.5 px-3 py-2 text-sm border-b-2 -mb-px transition-colors",
                active ? "border-primary text-foreground font-medium" : "border-transparent text-muted-foreground hover:text-foreground")}
            >
              <Icon className="w-4 h-4" /> {t.label}
            </button>
          );
        })}
      </div>
      {tab === "users" ? <UsersTab /> : null}
      {tab === "statuses" ? <StatusesTab /> : null}
      {tab === "priorities" ? <PrioritiesTab /> : null}
      {tab === "types" ? <TypesTab /> : null}
      {tab === "email" ? <EmailTab /> : null}
    </div>
  );
}

// ------- Users -------
function UsersTab() {
  const { refreshRefs } = useApp();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => setUsers((await api.get("/users")).data);
  useEffect(() => { load(); }, []);

  const del = async (u) => {
    if (!window.confirm(`Delete ${u.name}?`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success("User deleted"); load(); refreshRefs?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <Card className="p-5">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h2 className="font-display text-lg font-semibold">Team members ({users.length})</h2>
          <p className="text-xs text-muted-foreground mt-0.5">Invite people, assign roles, and reset passwords.</p>
        </div>
        <Button onClick={() => { setEditing(null); setOpen(true); }} data-testid="invite-user-btn" className="gap-1.5">
          <UserPlus className="w-4 h-4" /> Invite user
        </Button>
      </div>
      <div className="divide-y divide-border">
        {users.map((u) => (
          <div key={u.id} data-testid={`admin-user-${u.email}`} className="flex items-center gap-3 py-2.5 group">
            <Avatar className="w-9 h-9">
              <AvatarImage src={u.avatar_url} />
              <AvatarFallback className="text-xs">{initials(u.name)}</AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">{u.name}</div>
              <div className="text-xs text-muted-foreground truncate">{u.email} · {u.job_title || "—"}</div>
            </div>
            <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground bg-secondary px-1.5 py-0.5 rounded">{u.role}</span>
            <Button size="icon" variant="ghost" onClick={() => { setEditing(u); setOpen(true); }} data-testid={`edit-user-${u.email}`}><Pencil className="w-4 h-4" /></Button>
            <Button size="icon" variant="ghost" onClick={() => del(u)} data-testid={`del-user-${u.email}`}><Trash2 className="w-4 h-4" /></Button>
          </div>
        ))}
      </div>
      <UserDialog open={open} setOpen={setOpen} editing={editing} onSaved={() => { load(); refreshRefs?.(); }} />
    </Card>
  );
}

function UserDialog({ open, setOpen, editing, onSaved }) {
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "developer", job_title: "", avatar_url: "" });
  useEffect(() => {
    if (open) {
      setForm(editing
        ? { name: editing.name || "", email: editing.email || "", password: "", role: editing.role || "developer", job_title: editing.job_title || "", avatar_url: editing.avatar_url || "" }
        : { name: "", email: "", password: "", role: "developer", job_title: "", avatar_url: "" });
    }
  }, [open, editing]);
  const save = async () => {
    try {
      if (editing) {
        const payload = { name: form.name, role: form.role, job_title: form.job_title, avatar_url: form.avatar_url };
        if (form.password) payload.password = form.password;
        await api.patch(`/users/${editing.id}`, payload);
        toast.success("User updated");
      } else {
        if (!form.email || !form.name || !form.password) { toast.error("Email, name and password required"); return; }
        await api.post("/users", form);
        toast.success("User invited");
      }
      setOpen(false);
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent data-testid="user-dialog">
        <DialogHeader><DialogTitle className="font-display">{editing ? "Edit user" : "Invite user"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="ud-name" /></div>
            <div><Label>Email</Label><Input type="email" value={form.email} disabled={!!editing} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="ud-email" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="ud-role"><SelectValue /></SelectTrigger>
                <SelectContent>{ROLES.map((r) => (<SelectItem key={r} value={r}>{r}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <div><Label>Job title</Label><Input value={form.job_title} onChange={(e) => setForm({ ...form, job_title: e.target.value })} /></div>
          </div>
          <div><Label>{editing ? "New password (leave blank to keep)" : "Password"}</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="ud-password" /></div>
          <div><Label>Avatar URL (optional)</Label><Input value={form.avatar_url} onChange={(e) => setForm({ ...form, avatar_url: e.target.value })} placeholder="https://…" /></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={save} data-testid="ud-save">{editing ? "Save" : "Invite"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ------- Statuses / Priorities / Types (generic list editor) -------
function ListEditor({ label, items, setItems, extraCols, testidPrefix, defaults }) {
  const move = (idx, delta) => {
    const next = [...items];
    const to = idx + delta;
    if (to < 0 || to >= next.length) return;
    [next[idx], next[to]] = [next[to], next[idx]];
    setItems(next);
  };
  const del = (idx) => {
    if (!window.confirm(`Delete "${items[idx].name}"?`)) return;
    setItems(items.filter((_, i) => i !== idx));
  };
  const add = () => setItems([...items, defaults()]);
  return (
    <div className="space-y-2">
      {items.map((it, i) => (
        <div key={i} className="flex items-center gap-2 border border-border rounded-md p-2" data-testid={`${testidPrefix}-row-${i}`}>
          <div className="flex flex-col">
            <button onClick={() => move(i, -1)} className="text-muted-foreground hover:text-foreground" data-testid={`${testidPrefix}-up-${i}`}><ChevronUp className="w-3 h-3" /></button>
            <button onClick={() => move(i, 1)} className="text-muted-foreground hover:text-foreground" data-testid={`${testidPrefix}-down-${i}`}><ChevronDown className="w-3 h-3" /></button>
          </div>
          <Input value={it.id} onChange={(e) => setItems(items.map((x, ix) => ix === i ? { ...x, id: e.target.value.toLowerCase().replace(/\s+/g, "_") } : x))} placeholder="id" className="w-32 font-mono text-xs" data-testid={`${testidPrefix}-id-${i}`} />
          <Input value={it.name} onChange={(e) => setItems(items.map((x, ix) => ix === i ? { ...x, name: e.target.value } : x))} placeholder="Name" className="flex-1" data-testid={`${testidPrefix}-name-${i}`} />
          <Select value={it.color || "slate"} onValueChange={(v) => setItems(items.map((x, ix) => ix === i ? { ...x, color: v } : x))}>
            <SelectTrigger className="w-32" data-testid={`${testidPrefix}-color-${i}`}><SelectValue /></SelectTrigger>
            <SelectContent>{COLORS.map((c) => (<SelectItem key={c} value={c}><span className={cn("inline-block w-2.5 h-2.5 rounded-full mr-2 align-middle", `bg-${c}-500`)} />{c}</SelectItem>))}</SelectContent>
          </Select>
          {extraCols ? extraCols(it, i) : null}
          <Button size="icon" variant="ghost" onClick={() => del(i)} data-testid={`${testidPrefix}-del-${i}`}><Trash2 className="w-4 h-4" /></Button>
        </div>
      ))}
      <Button variant="outline" size="sm" onClick={add} className="gap-1.5" data-testid={`${testidPrefix}-add`}><Plus className="w-4 h-4" /> Add {label}</Button>
    </div>
  );
}

function SaveBar({ dirty, save, reset, testid }) {
  return (
    <div className="flex items-center gap-2 mt-4 pt-3 border-t border-border">
      <div className="text-xs text-muted-foreground">{dirty ? "You have unsaved changes" : "All changes saved"}</div>
      <div className="ml-auto flex gap-2">
        <Button variant="ghost" onClick={reset} disabled={!dirty}>Reset</Button>
        <Button onClick={save} disabled={!dirty} data-testid={testid}>Save changes</Button>
      </div>
    </div>
  );
}

function StatusesTab() {
  const { settings, refresh } = useSettings();
  const [items, setItems] = useState(settings.statuses || []);
  useEffect(() => { setItems(settings.statuses || []); }, [settings.statuses]);
  const dirty = JSON.stringify(items) !== JSON.stringify(settings.statuses || []);
  const save = async () => { await api.patch("/settings", { statuses: items }); toast.success("Statuses saved"); refresh(); };
  return (
    <Card className="p-5">
      <h2 className="font-display text-lg font-semibold mb-1">Statuses</h2>
      <p className="text-xs text-muted-foreground mb-4">Define the states an issue can be in. Reorder to change board column order.</p>
      <ListEditor
        label="status" items={items} setItems={setItems} testidPrefix="status"
        defaults={() => ({ id: `custom_${Date.now()}`, name: "New status", color: "slate", category: "todo" })}
        extraCols={(it, i) => (
          <Select value={it.category || "todo"} onValueChange={(v) => setItems(items.map((x, ix) => ix === i ? { ...x, category: v } : x))}>
            <SelectTrigger className="w-32" data-testid={`status-cat-${i}`}><SelectValue /></SelectTrigger>
            <SelectContent>{CATEGORIES.map((c) => (<SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>))}</SelectContent>
          </Select>
        )}
      />
      <SaveBar dirty={dirty} save={save} reset={() => setItems(settings.statuses || [])} testid="save-statuses" />
    </Card>
  );
}

function PrioritiesTab() {
  const { settings, refresh } = useSettings();
  const [items, setItems] = useState(settings.priorities || []);
  useEffect(() => { setItems(settings.priorities || []); }, [settings.priorities]);
  const dirty = JSON.stringify(items) !== JSON.stringify(settings.priorities || []);
  const save = async () => {
    const withOrder = items.map((it, i) => ({ ...it, order: i + 1 }));
    await api.patch("/settings", { priorities: withOrder });
    toast.success("Priorities saved");
    refresh();
  };
  return (
    <Card className="p-5">
      <h2 className="font-display text-lg font-semibold mb-1">Priorities</h2>
      <p className="text-xs text-muted-foreground mb-4">Highest-to-lowest ranking used across issues, filters and automation.</p>
      <ListEditor label="priority" items={items} setItems={setItems} testidPrefix="prio"
        defaults={() => ({ id: `custom_${Date.now()}`, name: "New", color: "slate" })} />
      <SaveBar dirty={dirty} save={save} reset={() => setItems(settings.priorities || [])} testid="save-priorities" />
    </Card>
  );
}

function TypesTab() {
  const { settings, refresh } = useSettings();
  const [items, setItems] = useState(settings.issue_types || []);
  useEffect(() => { setItems(settings.issue_types || []); }, [settings.issue_types]);
  const dirty = JSON.stringify(items) !== JSON.stringify(settings.issue_types || []);
  const save = async () => { await api.patch("/settings", { issue_types: items }); toast.success("Issue types saved"); refresh(); };
  return (
    <Card className="p-5">
      <h2 className="font-display text-lg font-semibold mb-1">Issue types</h2>
      <p className="text-xs text-muted-foreground mb-4">Add custom types (e.g. Change, Incident) alongside the built-ins.</p>
      <ListEditor label="type" items={items} setItems={setItems} testidPrefix="type"
        defaults={() => ({ id: `custom_${Date.now()}`, name: "New type", color: "slate", hierarchy: "child" })}
        extraCols={(it, i) => (
          <Select value={it.hierarchy || "child"} onValueChange={(v) => setItems(items.map((x, ix) => ix === i ? { ...x, hierarchy: v } : x))}>
            <SelectTrigger className="w-32" data-testid={`type-hier-${i}`}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="epic">Epic</SelectItem>
              <SelectItem value="child">Child</SelectItem>
              <SelectItem value="subtask">Subtask</SelectItem>
            </SelectContent>
          </Select>
        )}
      />
      <SaveBar dirty={dirty} save={save} reset={() => setItems(settings.issue_types || [])} testid="save-types" />
    </Card>
  );
}

// ------- Email & SMTP -------
function EmailTab() {
  const [mailStatus, setMailStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testEmail, setTestEmail] = useState("info@pulsarcityambassador.com");
  const [sendingTest, setSendingTest] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [enabled, setEnabled] = useState(true);
  const [savingEnabled, setSavingEnabled] = useState(false);

  const loadStatus = async () => {
    setLoading(true);
    try {
      const res = await api.get("/settings/mail-status");
      setMailStatus(res.data);
      setEnabled(res.data.enabled !== false);
    } catch (e) {
      toast.error("Failed to load mail status");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStatus();
  }, []);

  const toggleEnabled = async () => {
    const nextVal = !enabled;
    setSavingEnabled(true);
    try {
      await api.patch("/settings", { email_notifications_enabled: nextVal });
      setEnabled(nextVal);
      toast.success(nextVal ? "Email notifications enabled" : "Email notifications disabled");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update notification settings");
    } finally {
      setSavingEnabled(false);
    }
  };

  const handleSendTest = async (e) => {
    e?.preventDefault();
    if (!testEmail || !testEmail.includes("@")) {
      toast.error("Please enter a valid email address");
      return;
    }
    setSendingTest(true);
    setTestResult(null);
    try {
      const res = await api.post("/settings/test-email", { to_email: testEmail });
      setTestResult({ success: true, message: res.data.message || "Test email sent successfully!" });
      toast.success("Test email delivered successfully!");
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || "Failed to send test email";
      setTestResult({ success: false, message: msg });
      toast.error(msg);
    } finally {
      setSendingTest(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card className="p-6">
        <div className="flex items-start justify-between mb-6 pb-4 border-b border-border">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="font-display text-xl font-semibold">Jira Mail Notification System</h2>
              <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-500/10 text-emerald-600 border border-emerald-500/20">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                Active SMTP Relay
              </span>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Automated Jira-style emails for ticket creation, updates, status transitions, reassignments, comments, and attachments with CCs to watchers and reporters.
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={loadStatus}
            disabled={loading}
            className="gap-1.5 text-xs"
          >
            <RefreshCw className={cn("w-3.5 h-3.5", loading && "animate-spin")} /> Refresh
          </Button>
        </div>

        {/* Configuration Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="p-3.5 rounded-lg border bg-muted/30">
            <div className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">SMTP Server</div>
            <div className="text-sm font-mono font-medium mt-1 text-foreground">{mailStatus?.host || "smtp-pulse.com"}</div>
            <div className="text-[11px] text-muted-foreground mt-0.5">Port {mailStatus?.port || "587"} (STARTTLS / SSL)</div>
          </div>
          <div className="p-3.5 rounded-lg border bg-muted/30">
            <div className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">Authenticated Sender</div>
            <div className="text-sm font-mono font-medium mt-1 text-foreground truncate" title={mailStatus?.user}>{mailStatus?.user || "info@pulsarcityambassador.com"}</div>
            <div className="text-[11px] text-muted-foreground mt-0.5">TLS Secured Connection</div>
          </div>
          <div className="p-3.5 rounded-lg border bg-muted/30">
            <div className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">Display Name</div>
            <div className="text-sm font-medium mt-1 text-foreground">{mailStatus?.from_name || "DigiJira Notifications"}</div>
            <div className="text-[11px] text-muted-foreground mt-0.5">&lt;{mailStatus?.from_email || "info@pulsarcityambassador.com"}&gt;</div>
          </div>
          <div className="p-3.5 rounded-lg border bg-muted/30 flex flex-col justify-between">
            <div className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">Global Notifications</div>
            <div className="flex items-center justify-between mt-2">
              <span className={cn("text-xs font-semibold", enabled ? "text-emerald-600" : "text-muted-foreground")}>
                {enabled ? "Enabled" : "Disabled"}
              </span>
              <Button
                variant={enabled ? "default" : "outline"}
                size="xs"
                onClick={toggleEnabled}
                disabled={savingEnabled}
                data-testid="toggle-email-btn"
              >
                {enabled ? "Turn Off" : "Turn On"}
              </Button>
            </div>
          </div>
        </div>

        {/* Test Email Section */}
        <div className="p-5 rounded-xl border bg-card/60 space-y-4">
          <div>
            <h3 className="text-sm font-semibold flex items-center gap-2">
              <Send className="w-4 h-4 text-primary" /> Test SMTP Delivery
            </h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              Send an instant verification email to test connectivity with the SMTP server and verify deliverability.
            </p>
          </div>

          <form onSubmit={handleSendTest} className="flex flex-col sm:flex-row gap-3 max-w-xl">
            <Input
              type="email"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              placeholder="recipient@example.com"
              className="flex-1 text-sm"
              data-testid="test-email-input"
              required
            />
            <Button
              type="submit"
              disabled={sendingTest}
              className="gap-2 shrink-0"
              data-testid="send-test-email-btn"
            >
              {sendingTest ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" /> Sending…
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" /> Send Test Email
                </>
              )}
            </Button>
          </form>

          {testResult && (
            <div
              className={cn(
                "p-3.5 rounded-lg border text-xs flex items-start gap-2.5 transition-all",
                testResult.success
                  ? "bg-emerald-500/10 border-emerald-500/20 text-emerald-800 dark:text-emerald-300"
                  : "bg-destructive/10 border-destructive/20 text-destructive dark:text-red-400"
              )}
              data-testid="test-email-result"
            >
              {testResult.success ? (
                <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
              ) : (
                <AlertCircle className="w-4 h-4 text-destructive shrink-0 mt-0.5" />
              )}
              <div>
                <div className="font-semibold">{testResult.success ? "Success" : "Delivery Error"}</div>
                <div className="mt-0.5 leading-relaxed">{testResult.message}</div>
              </div>
            </div>
          )}
        </div>

        {/* Email Batching & Outbox Queue Status Card */}
        <div className="p-5 rounded-xl border bg-muted/20 mt-6 space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-semibold flex items-center gap-2">
                  <Clock className="w-4 h-4 text-primary" /> Jira Notification Batching (Clubbed Updates)
                </h3>
                <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium bg-primary/10 text-primary border border-primary/20">
                  {mailStatus?.batching?.enabled !== false ? "Batching Enabled" : "Instant Mode"}
                </span>
              </div>
              <p className="text-xs text-muted-foreground mt-0.5">
                Multiple rapid updates on the same ticket are grouped into a single consolidated email over a 2-minute debounce window (max 5 minutes).
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={async () => {
                try {
                  const res = await api.post("/settings/flush-email-queue");
                  toast.success(res.data.message || "Email queue flushed successfully!");
                  loadStatus();
                } catch (e) {
                  toast.error("Failed to flush email queue");
                }
              }}
              className="gap-1.5 shrink-0 text-xs"
              data-testid="flush-queue-btn"
            >
              <Send className="w-3.5 h-3.5" /> Flush Queue Now
            </Button>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-2">
            <div className="p-3 rounded-lg border bg-card text-center">
              <div className="text-[10px] uppercase font-semibold tracking-wider text-muted-foreground">Pending Events</div>
              <div className="text-lg font-bold text-foreground mt-0.5">{mailStatus?.batching?.pending_events ?? 0}</div>
              <div className="text-[10px] text-muted-foreground mt-0.5">{mailStatus?.batching?.pending_issues_count ?? 0} ticket(s) waiting</div>
            </div>
            <div className="p-3 rounded-lg border bg-card text-center">
              <div className="text-[10px] uppercase font-semibold tracking-wider text-muted-foreground">Debounce Window</div>
              <div className="text-lg font-bold text-foreground mt-0.5">{mailStatus?.batching?.window_seconds ?? 120}s</div>
              <div className="text-[10px] text-muted-foreground mt-0.5">2 min inactivity buffer</div>
            </div>
            <div className="p-3 rounded-lg border bg-card text-center">
              <div className="text-[10px] uppercase font-semibold tracking-wider text-muted-foreground">Max Batch Wait</div>
              <div className="text-lg font-bold text-foreground mt-0.5">{mailStatus?.batching?.max_wait_seconds ?? 300}s</div>
              <div className="text-[10px] text-muted-foreground mt-0.5">5 min max before send</div>
            </div>
            <div className="p-3 rounded-lg border bg-card text-center">
              <div className="text-[10px] uppercase font-semibold tracking-wider text-muted-foreground">Worker Status</div>
              <div className="text-lg font-bold text-emerald-600 mt-0.5">
                {mailStatus?.batching?.worker_running !== false ? "Running" : "Idle"}
              </div>
              <div className="text-[10px] text-muted-foreground mt-0.5">{mailStatus?.batching?.sent_events ?? 0} total dispatched</div>
            </div>
          </div>
        </div>
      </Card>

      {/* Jira Notification Specifications Card */}
      <Card className="p-6">
        <h3 className="font-display text-base font-semibold mb-3">Jira-Standard Notification Workflow</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs text-muted-foreground leading-relaxed">
          <div className="p-3.5 rounded-lg border bg-muted/10 space-y-1">
            <div className="font-semibold text-foreground">👥 Intelligent To / CC Routing</div>
            <p>Assignees and @mentioned team members receive direct <code className="text-primary font-mono">To:</code> emails. Watchers and reporters are automatically added in <code className="text-primary font-mono">Cc:</code>.</p>
          </div>
          <div className="p-3.5 rounded-lg border bg-muted/10 space-y-1">
            <div className="font-semibold text-foreground">📊 Visual Change-Diff Tables</div>
            <p>Ticket edits render before-and-after change tables highlighting previous and new values, status badges, and priority updates.</p>
          </div>
          <div className="p-3.5 rounded-lg border bg-muted/10 space-y-1">
            <div className="font-semibold text-foreground">🧵 Email Thread Grouping</div>
            <p>Custom <code className="text-primary font-mono">In-Reply-To</code> and <code className="text-primary font-mono">References</code> headers keep all notifications for the same ticket grouped into a single email thread in Gmail and Outlook.</p>
          </div>
        </div>
      </Card>
    </div>
  );
}
